import { useEffect, useRef, useState } from "react";
import { Building2, Check } from "lucide-react";
import api from "../lib/api";

/**
 * Company name input with a debounced typeahead dropdown backed by
 * `/api/companies/search`. Picking a suggestion sets BOTH the string name and
 * the `company_id` foreign key so the created record links to the catalog.
 *
 * Props:
 *   value:        the string company name (source of truth in the parent form)
 *   companyId:    the currently linked company_id (or null)
 *   onChange({ name, company_id })  — called on every keystroke AND on pick
 *   inputTestId:  data-testid for the underlying <input>
 */
export default function CompanyAutocomplete({ value, companyId, onChange, inputTestId = "company-input" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIx, setActiveIx] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = (q) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/companies/search", { params: { q, limit: 8 } });
        setSuggestions(data || []);
      } catch { setSuggestions([]); }
      setLoading(false);
    }, 180);
  };

  const handleChange = (e) => {
    const name = e.target.value;
    // Any manual edit breaks the previously-picked company_id link.
    onChange({ name, company_id: null });
    if (name.trim().length >= 1) { setOpen(true); fetchSuggestions(name.trim()); }
    else { setOpen(false); setSuggestions([]); }
  };

  const pick = (row) => {
    onChange({ name: row.name, company_id: row.id });
    setOpen(false);
    setActiveIx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && activeIx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <input
          className="pg-input"
          value={value || ""}
          onChange={handleChange}
          onFocus={() => { if ((value || "").trim().length >= 1) { fetchSuggestions(value.trim()); setOpen(true); } }}
          onKeyDown={onKeyDown}
          placeholder="Start typing…"
          data-testid={inputTestId}
          style={companyId ? { paddingRight: 28 } : undefined}
          autoComplete="off"
        />
        {companyId && (
          <span
            title="Linked to an existing company in your catalog"
            data-testid={`${inputTestId}-linked`}
            style={{
              position: "absolute", right: 8, top: "50%",
              transform: "translateY(-50%)",
              color: "var(--pg-accent-deep, #34a853)",
              display: "inline-flex", alignItems: "center",
            }}
          >
            <Check size={14} strokeWidth={2.6} />
          </span>
        )}
      </div>
      {open && (loading || suggestions.length > 0) && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            background: "var(--pg-surface)", border: "1px solid var(--pg-border)",
            borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
            zIndex: 60, maxHeight: 260, overflowY: "auto",
          }}
          data-testid={`${inputTestId}-suggestions`}
        >
          {loading && suggestions.length === 0 ? (
            <div className="mono text-[11px] text-mute" style={{ padding: "10px 12px" }}>Searching…</div>
          ) : (
            suggestions.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s)}
                onMouseEnter={() => setActiveIx(i)}
                data-testid={`${inputTestId}-suggestion-${s.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 12px", textAlign: "left", background: activeIx === i ? "var(--pg-surface-2)" : "transparent",
                  border: "none", borderBottom: "1px solid var(--pg-border)", cursor: "pointer",
                }}
              >
                <Building2 size={14} style={{ color: "var(--pg-text-mute)", flexShrink: 0 }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="text-sm" style={{ color: "var(--pg-text)" }}>{s.name}</div>
                  {(s.industry || s.website) && (
                    <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>
                      {[s.industry, s.website].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
